'use client';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
 import Icon from'@/components/ui/AppIcon';

interface Category {
  id: string
  name: string
  icon: string
  topicCount: number
  questionCount: number
  topics: Topic[]
}

interface Topic {
  id: string
  name: string
  questionCount: number
}

const CategorySelectionContent = () => {
  const router = useRouter()
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedTopics, setSelectedTopics] = useState<string[]>([])
  const [studyMode, setStudyMode] = useState<'study' | 'test'>('study')
  const [questionCount, setQuestionCount] = useState(25)
  const [timerDuration, setTimerDuration] = useState(30)

  const categories: Category[] = [
    {
      id: 'cat_nav',
      name: 'Navigation',
      icon: 'MapIcon',
      topicCount: 5,
      questionCount: 87,
      topics: [
        { id: 'topic_vor', name: 'VOR Navigation', questionCount: 23 },
        { id: 'topic_gps', name: 'GPS Systems', questionCount: 18 },
        { id: 'topic_charts', name: 'Aeronautical Charts', questionCount: 22 },
        { id: 'topic_compass', name: 'Compass Navigation', questionCount: 15 },
        { id: 'topic_dead', name: 'Dead Reckoning', questionCount: 9 },
      ],
    },
    {
      id: 'cat_weather',
      name: 'Meteorology',
      icon: 'CloudIcon',
      topicCount: 4,
      questionCount: 62,
      topics: [
        { id: 'topic_clouds', name: 'Cloud Types', questionCount: 15 },
        { id: 'topic_patterns', name: 'Weather Patterns', questionCount: 22 },
        { id: 'topic_hazards', name: 'Weather Hazards', questionCount: 18 },
        { id: 'topic_reports', name: 'Weather Reports', questionCount: 7 },
      ],
    },
    {
      id: 'cat_regs',
      name: 'Regulations',
      icon: 'DocumentTextIcon',
      topicCount: 6,
      questionCount: 95,
      topics: [
        { id: 'topic_airspace', name: 'Airspace Classes', questionCount: 31 },
        { id: 'topic_right', name: 'Right of Way Rules', questionCount: 18 },
        { id: 'topic_operations', name: 'Flight Operations', questionCount: 24 },
        { id: 'topic_licensing', name: 'Pilot Licensing', questionCount: 12 },
        { id: 'topic_medical', name: 'Medical Requirements', questionCount: 6 },
        { id: 'topic_radio', name: 'Radio Communications', questionCount: 4 },
      ],
    },
    {
      id: 'cat_systems',
      name: 'Aircraft Systems',
      icon: 'CogIcon',
      topicCount: 8,
      questionCount: 124,
      topics: [
        { id: 'topic_engine', name: 'Engine Systems', questionCount: 28 },
        { id: 'topic_electrical', name: 'Electrical Systems', questionCount: 19 },
        { id: 'topic_fuel', name: 'Fuel Systems', questionCount: 15 },
        { id: 'topic_hydraulic', name: 'Hydraulic Systems', questionCount: 12 },
        { id: 'topic_flight', name: 'Flight Controls', questionCount: 22 },
        { id: 'topic_landing', name: 'Landing Gear', questionCount: 11 },
        { id: 'topic_instruments', name: 'Flight Instruments', questionCount: 14 },
        { id: 'topic_avionics', name: 'Avionics', questionCount: 3 },
      ],
    },
    {
      id: 'cat_performance',
      name: 'Performance',
      icon: 'ChartBarIcon',
      topicCount: 3,
      questionCount: 54,
      topics: [
        { id: 'topic_weight', name: 'Weight & Balance', questionCount: 21 },
        { id: 'topic_takeoff', name: 'Takeoff Performance', questionCount: 18 },
        { id: 'topic_landing_perf', name: 'Landing Performance', questionCount: 15 },
      ],
    },
    {
      id: 'cat_aero',
      name: 'Aerodynamics',
      icon: 'RocketLaunchIcon',
      topicCount: 4,
      questionCount: 68,
      topics: [
        { id: 'topic_lift', name: 'Lift & Drag', questionCount: 24 },
        { id: 'topic_stability', name: 'Stability & Control', questionCount: 19 },
        { id: 'topic_stalls', name: 'Stalls & Spins', questionCount: 16 },
        { id: 'topic_loads', name: 'Load Factors', questionCount: 9 },
      ],
    },
  ]

  const selectedCategoryData = categories.find(cat => cat.id === selectedCategory)

  const handleTopicToggle = (topicId: string) => {
    setSelectedTopics(prev =>
      prev.includes(topicId)
        ? prev.filter(id => id !== topicId)
        : [...prev, topicId]
    )
  }

  const handleStartSession = () => {
    if (selectedTopics.length === 0) {
      alert('Please select at least one topic')
      return
    }
    router.push('/study-interface')
  }

  const totalSelectedQuestions = selectedCategoryData?.topics
    .filter(topic => selectedTopics.includes(topic.id))
    .reduce((sum, topic) => sum + topic.questionCount, 0) || 0

  return (
    <section className="min-h-screen bg-muted py-8 sm:py-12">
      <div className="container px-4 sm:px-6 max-w-7xl">
        <div className="mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2 sm:mb-3">Customize Your Study Session</h1>
          <p className="text-base sm:text-lg text-muted-foreground">Select topics, choose your mode, and set your preferences</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Left Column - Categories & Topics */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Step 1: Category Selection */}
            <div className="bg-card rounded-2xl p-4 sm:p-6 border border-border">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                  <span className="text-primary font-bold text-sm sm:text-base">1</span>
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-foreground">Select Category</h2>
                  <p className="text-xs sm:text-sm text-muted-foreground">Choose a subject area</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => {
                      setSelectedCategory(category.id)
                      setSelectedTopics([])
                    }}
                    className={`p-3 sm:p-4 rounded-xl border-2 transition-all ${
                      selectedCategory === category.id
                        ? 'border-primary bg-primary/10' :'border-border bg-background hover:border-primary/50'
                    }`}
                  >
                    <Icon name={category.icon as any} size={24} className={`mx-auto mb-1.5 sm:mb-2 sm:w-8 sm:h-8 ${
                      selectedCategory === category.id ? 'text-primary' : 'text-muted-foreground'
                    }`} />
                    <div className="text-xs sm:text-sm font-semibold text-foreground mb-0.5 sm:mb-1">{category.name}</div>
                    <div className="text-xs text-muted-foreground">{category.questionCount} questions</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Topic Selection */}
            {selectedCategoryData && (
              <div className="bg-card rounded-2xl p-4 sm:p-6 border border-border">
                <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-accent/10 rounded-xl flex items-center justify-center">
                    <span className="text-accent font-bold text-sm sm:text-base">2</span>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-foreground">Select Topics</h2>
                    <p className="text-xs sm:text-sm text-muted-foreground">Choose specific topics to study</p>
                  </div>
                </div>

                <div className="space-y-2">
                  {selectedCategoryData.topics.map((topic) => (
                    <label
                      key={topic.id}
                      className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-background rounded-xl border border-border hover:border-accent/50 transition-colors cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTopics.includes(topic.id)}
                        onChange={() => handleTopicToggle(topic.id)}
                        className="w-4 h-4 sm:w-5 sm:h-5 rounded border-border text-accent focus:ring-accent flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm sm:text-base font-semibold text-foreground">{topic.name}</div>
                        <div className="text-xs sm:text-sm text-muted-foreground">{topic.questionCount} questions</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Settings */}
          <div className="space-y-4 sm:space-y-6">
            {/* Step 3: Mode Selection */}
            <div className="bg-card rounded-2xl p-4 sm:p-6 border border-border lg:sticky lg:top-6">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-success/10 rounded-xl flex items-center justify-center">
                  <span className="text-success font-bold text-sm sm:text-base">3</span>
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-foreground">Study Mode</h2>
                  <p className="text-xs sm:text-sm text-muted-foreground">Choose your approach</p>
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <button
                  onClick={() => setStudyMode('study')}
                  className={`w-full p-3 sm:p-4 rounded-xl border-2 transition-all ${
                    studyMode === 'study' ?'border-success bg-success/10' :'border-border bg-background hover:border-success/50'
                  }`}
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Icon name="BookOpenIcon" size={20} className={`sm:w-6 sm:h-6 ${studyMode === 'study' ? 'text-success' : 'text-muted-foreground'}`} />
                    <div className="text-left flex-1">
                      <div className="text-sm sm:text-base font-semibold text-foreground">Study Mode</div>
                      <div className="text-xs text-muted-foreground">With instant explanations</div>
                    </div>
                    {studyMode === 'study' && <Icon name="CheckCircleIcon" size={18} className="text-success sm:w-5 sm:h-5 flex-shrink-0" />}
                  </div>
                </button>

                <button
                  onClick={() => setStudyMode('test')}
                  className={`w-full p-3 sm:p-4 rounded-xl border-2 transition-all ${
                    studyMode === 'test' ?'border-warning bg-warning/10' :'border-border bg-background hover:border-warning/50'
                  }`}
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Icon name="ClipboardDocumentCheckIcon" size={20} className={`sm:w-6 sm:h-6 ${studyMode === 'test' ? 'text-warning' : 'text-muted-foreground'}`} />
                    <div className="text-left flex-1">
                      <div className="text-sm sm:text-base font-semibold text-foreground">Test Mode</div>
                      <div className="text-xs text-muted-foreground">Exam simulation</div>
                    </div>
                    {studyMode === 'test' && <Icon name="CheckCircleIcon" size={18} className="text-warning sm:w-5 sm:h-5 flex-shrink-0" />}
                  </div>
                </button>
              </div>

              {/* Question Count */}
              <div className="mb-4 sm:mb-6">
                <label className="block text-sm font-medium text-foreground mb-2 sm:mb-3">Number of Questions</label>
                <select
                  value={questionCount}
                  onChange={(e) => setQuestionCount(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-background border border-input rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value={25}>25 Questions</option>
                  <option value={50}>50 Questions</option>
                  <option value={75}>75 Questions</option>
                  <option value={100}>100 Questions</option>
                </select>
              </div>

              {/* Timer Duration */}
              <div className="mb-4 sm:mb-6">
                <label className="block text-sm font-medium text-foreground mb-2 sm:mb-3">Time Limit</label>
                <select
                  value={timerDuration}
                  onChange={(e) => setTimerDuration(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-background border border-input rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value={30}>30 Minutes</option>
                  <option value={60}>1 Hour</option>
                  <option value={90}>1 Hour 30 Minutes</option>
                  <option value={120}>2 Hours</option>
                </select>
              </div>

              {/* Summary */}
              <div className="p-3 bg-muted rounded-xl mb-4 sm:mb-6">
                <div className="text-sm text-muted-foreground mb-1">Session Summary</div>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Selected Topics:</span>
                    <span className="font-semibold text-foreground">{selectedTopics.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Available Questions:</span>
                    <span className="font-semibold text-foreground">{totalSelectedQuestions}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Mode:</span>
                    <span className="font-semibold text-foreground capitalize">{studyMode}</span>
                  </div>
                </div>
              </div>

              {/* Start Button */}
              <button
                onClick={handleStartSession}
                disabled={selectedTopics.length === 0}
                className="w-full px-4 py-3 bg-primary text-primary-foreground font-bold text-lg rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                Start Study Session
                <Icon name="ArrowRightIcon" size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default CategorySelectionContent